import { useCallback, useEffect, useState } from 'react';
import { ApiFout, api } from '@/lib/api';
import type { AuthStatus, Beheerder } from '@/lib/api-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Plus, ShieldMinus, UserCog, MailCheck, MailQuestion } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Wie er beheerder is.
 *
 * Dit tabblad beheerde wachtwoorden: aanmaken met een wachtwoord van minimaal acht
 * tekens, en resetten via een `prompt()` waarin de beheerder een nieuw wachtwoord
 * typte. Dat is er niet meer. Entra doet de authenticatie, dus er valt hier geen
 * wachtwoord te zetten -- en er valt er ook geen te lekken.
 *
 * Wat blijft is de autorisatie: welke accounts de beheerdersrol hebben. Die staat in
 * user_roles, want de 61 RLS-policies leunen erop via has_role(auth.uid(), 'admin').
 * Entra neemt dat niet over.
 *
 * Uitnodigen gaat op e-mailadres. Bij de eerste aanmelding hecht het Entra-account
 * zich aan de rij met dat adres; niemand hoeft een object-id over te typen.
 */
export function AdminUsersTab() {
  const [admins, setAdmins] = useState<Beheerder[]>([]);
  const [loading, setLoading] = useState(true);
  const [eigenUserId, setEigenUserId] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lijst, profiel] = await Promise.all([
        api.get<{ admins: Beheerder[] }>('/admin/admins'),
        // Om te weten welke rij "u" is. De API laat een beheerder zijn eigen rol niet
        // intrekken -- dat is wat garandeert dat er altijd één overblijft -- en die
        // knop hoort dan ook uitgeschakeld te zijn.
        api.get<{ profile: { userId: string } | null }>('/admin/profile'),
      ]);
      setAdmins(lijst.admins);
      setEigenUserId(profiel.profile?.userId ?? null);
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon de beheerders niet laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleInvite = async () => {
    if (!newEmail.trim() || newName.trim().length < 2) {
      toast.error('Vul een e-mailadres en een naam in');
      return;
    }

    setIsCreating(true);
    try {
      const antwoord = await api.post<{ ok: boolean; volgendeStap: string }>('/admin/admins', {
        email: newEmail.trim(),
        fullName: newName.trim(),
      });
      toast.success('Beheerder uitgenodigd', { description: antwoord.volgendeStap });
      setNewEmail('');
      setNewName('');
      await load();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Uitnodigen mislukt');
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevoke = async (beheerder: Beheerder) => {
    if (!confirm(`Beheerdersrechten intrekken voor ${beheerder.email}?`)) return;

    try {
      await api.delete(`/admin/admins/${beheerder.userId}`);
      toast.success('Beheerdersrechten ingetrokken');
      await load();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Intrekken mislukt');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCog className="h-5 w-5" />
          Beheerders
        </CardTitle>
        <CardDescription>
          Wie toegang heeft tot dit beheerpaneel. Aanmelden gaat met Microsoft Entra ID; wachtwoorden
          worden niet door deze applicatie beheerd.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <div className="space-y-2">
              {admins.map((a) => (
                <div key={a.userId} className="flex items-center gap-2 rounded-md border p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {a.fullName || a.email}
                      {a.userId === eigenUserId && (
                        <span className="ml-2 text-xs text-muted-foreground">(u)</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{a.email}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      {a.entraGekoppeld ? (
                        <>
                          <MailCheck className="h-3 w-3" />
                          Gekoppeld aan Entra
                          {a.lastSeenAt && (
                            <> · laatst actief {new Date(a.lastSeenAt).toLocaleDateString('nl-NL')}</>
                          )}
                        </>
                      ) : (
                        <>
                          {/*
                            Uitgenodigd maar nog nooit aangemeld. In de bron was dat
                            niet te zien: een account zonder aanmelding zag er
                            hetzelfde uit als een actief account.
                          */}
                          <MailQuestion className="h-3 w-3" />
                          Uitgenodigd, nog niet aangemeld
                        </>
                      )}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 text-destructive"
                    onClick={() => void handleRevoke(a)}
                    title={
                      a.userId === eigenUserId
                        ? 'U kunt uw eigen rechten niet intrekken'
                        : 'Beheerdersrechten intrekken'
                    }
                    disabled={a.userId === eigenUserId}
                  >
                    <ShieldMinus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {admins.length === 0 && (
                <p className="text-sm text-muted-foreground">Geen beheerders gevonden.</p>
              )}
            </div>

            <div className="space-y-2 pt-2 border-t">
              <p className="text-sm font-medium">Beheerder uitnodigen</p>
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="naam@isoft.nl"
                  className="text-sm"
                />
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Volledige naam"
                  className="text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void handleInvite()}
                  disabled={isCreating}
                  className="shrink-0"
                >
                  {isCreating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-1" />
                      Uitnodigen
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Er gaat geen e-mail uit. Voeg het account daarna toe aan de Entra-groep die toegang
                heeft tot deze app; de koppeling wordt bij de eerste aanmelding automatisch gelegd op
                e-mailadres.
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
